/**
 * Williams thread storage semantics.
 *
 * Staff chat is "fresh by default": opening the Williams surface starts a new
 * conversation, and the previous one stays resumable behind a
 * "Continue last thread" affordance (docs/architecture/chat-ui-direction.md §6).
 * That needs two keys, not one — the ACTIVE thread the composer talks to, and
 * the LAST thread that was retired and can be resumed.
 *
 * These helpers are the single writer of both keys so the widget and the
 * Williams page cannot drift apart on the retire/resume semantics. Every access
 * is try/catch-guarded: Safari private mode and some embedded webviews throw on
 * `localStorage` access, and a chat surface must never fail to render because a
 * thread id could not be read.
 */

export const ACTIVE_KEY = 'trf_williams_conversation_id';
export const LAST_KEY = 'trf_williams_last_conversation_id';

function readKey(key: string): string | null {
  try {
    const value = localStorage.getItem(key);
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — thread continuity degrades, chat still works */
  }
}

function clearKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable — nothing to clean up */
  }
}

/** The conversation the Williams composer is currently attached to. */
export function getActiveThreadId(): string | null {
  return readKey(ACTIVE_KEY);
}

/**
 * Point the active slot at `id`. An empty/blank id clears the slot rather than
 * storing a falsy string that later reads would hand back as a real thread.
 */
export function setActiveThreadId(id: string | null | undefined): void {
  if (!id || !id.trim()) {
    clearKey(ACTIVE_KEY);
    return;
  }
  writeKey(ACTIVE_KEY, id);
}

/** The most recently retired conversation, offered as "Continue last thread". */
export function getLastThreadId(): string | null {
  return readKey(LAST_KEY);
}

/**
 * Retire the active thread: active → last, active cleared. Returns the retired
 * id so the caller can disconnect its stream, or null when there was nothing
 * active (in which case the existing "last" value is left untouched — a no-op
 * retire must not destroy a still-resumable thread).
 */
export function retireActiveThread(): string | null {
  const active = getActiveThreadId();
  if (!active) return null;
  writeKey(LAST_KEY, active);
  clearKey(ACTIVE_KEY);
  return active;
}
